import React, {
    Children,
    createContext,
    useMemo,
    useState,
    PropsWithChildren,
    useCallback,
    ComponentType,
} from 'react';
import fetch from 'isomorphic-fetch';
import { SDKWithGuest } from '@ringcentral/guest';
import { events as DiscoveryEvents } from '@ringcentral/sdk/lib/platform/Discovery';

import getConfig from '@ringcentral/rwc-utils/src/config';
import { getPrefixedLogger, prefixes } from '@ringcentral/rwc-utils/src/rwc-logger';
import { getPatchedFetch } from '@low/patch-fetch';
import { webClientReleaseVersion } from '@low/web-client-info';
import BasicSessionManager from '@low/containers/basic-session-manager';
import SDKGenerator from '@low/sdk-generator';
import { hasAuthUserToken } from '@low/validate-token';
import { getBrandId } from '@ringcentral/rwc-branding/src/utils';
import discoveryOption from '@low/utils/get-discovery-option';
import '@low/utils/auth';
import { AuthStatus } from '@low/types';
import { getDeveloperFlag } from '@ringcentral/rwc-utils/src/developer';
import { isIOSWebKit } from '@ringcentral/rwc-utils/src/client-detection';
import InMemorySdkStorage from '@rwc/utils/in-memory-storage';

const SDK = SDKGenerator(SDKWithGuest);
export type SDK = InstanceType<typeof SDK>;
export type SDKGetter = () => SDK;

const PASConf = getConfig('PAS');

export const patchedFetch = getPatchedFetch(fetch);

export const SdkContext = createContext<{ sdk: SDKGetter; authStatus: AuthStatus } | null>(null);

const logger = getPrefixedLogger(prefixes.VC_CONTAINERS, 'sdk-context');

export const SdkContextProvider = ({
    children,
    SessionManager,
}: PropsWithChildren<{
    SessionManager?: ComponentType<
        PropsWithChildren<{
            sdk: () => SDK;
            setAuthStatus: (authStatus: AuthStatus) => void;
        }>
    >;
}>) => {
    const [authStatus, setAuthStatus] = useState(AuthStatus.PENDING);
    const sdk = useMemo<SDK>(() => {
        logger.info('sdk initializing');
        const maybeAuthorized = hasAuthUserToken();

        // iOS WebKit's ITP can wipe localStorage for unauth'd guests mid-flow — keep SDK state in memory.
        const shouldUseMemoryStorage = !maybeAuthorized && isIOSWebKit;

        logger.info(
            `shouldUseMemoryStorage: ${maybeAuthorized} ${isIOSWebKit} ${shouldUseMemoryStorage}`
        );

        const sdkInstance = new SDK({
            fetch: patchedFetch,
            brandId: getBrandId(),
            clientId: PASConf.clientId as string,
            appName: PASConf.userAgentAppName as string,
            appVersion: webClientReleaseVersion,
            redirectUri: `${location.origin}`,
            clearCacheOnRefreshError: false,
            enableDiscovery: true,
            discoveryInitialEndpoint: discoveryOption.initialEndpoint,
            discoveryServer: discoveryOption.server,
            discoveryAutoInit: true,
            localStorage: shouldUseMemoryStorage ? new InMemorySdkStorage() : window.localStorage,
        });

        const discoveryFailedCallback = e => {
            logger.error('discovery failed', e);
        };

        sdkInstance
            .platform()
            .discovery()
            .on(DiscoveryEvents.initialFetchError, discoveryFailedCallback);
        sdkInstance
            .platform()
            .discovery()
            .on(DiscoveryEvents.externalRefreshError, discoveryFailedCallback);

        return sdkInstance;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSdk = useCallback<SDKGetter>(() => sdk, [sdk]);

    if (getDeveloperFlag()) {
        window[Symbol.for('rcSdk')] = sdk;
    }

    const FinalSessionManager = SessionManager ? SessionManager : BasicSessionManager;

    return (
        <SdkContext.Provider value={{ sdk: getSdk, authStatus }}>
            {/* TODO: RCV-99509 fix ts after update ts */}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore*/}
            <FinalSessionManager sdk={getSdk} setAuthStatus={setAuthStatus} />
            {Children.only(children)}
        </SdkContext.Provider>
    );
};
